import React, { JSX, useMemo, useRef, useState } from 'react';
import { SingleValue } from 'react-select';
import { Box, Button, Checkbox, Flex, HStack, Image, Text, useDisclosure, VStack } from '@chakra-ui/react';

import Dialog from '../../../../../common/components/ConfirmationDialog/Dialog';
import { RestaurantSelect } from '../../../../../common/components/RestaurantSelect/RestaurantSelect';
import { SelectOption } from '../../../../../common/components/Select/model/selectOption';
import Step from '../../../../../common/components/Stepper/Step';
import Stepper from '../../../../../common/components/Stepper/Stepper';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { useScannedRelevosContext } from '../../../../../contexts/ScannedRelevosContext';
import { useRestaurantsData } from '../../../../../services/restaurants';
import colors from '../../../../../styles/colors';
import { QuickContactType } from '../../../../QuickContacts/model/enums/quick-contact-type.enum';
import { QuickContactResponse } from '../../../../QuickContacts/model/quick-contact-response';
import { QuickContactSelect } from '../../../components/QuickContactSelect/QuickContactSelect';
import Autocomplete from '../../../components/UserSelect/Autocomplete';
import ScanUserIdModal from '../../../components/UserSelect/ScanUserIdModal';
import { ItemAssignmentSelectMode, UserSelectMode } from '../../../components/UserSelect/types';
import { ScannerMode } from '../../../model/enums/scanner-mode.enum';
import { getAssignSelectStyle } from '../../../styles/relevo-assign-select-style';
import { WarningText } from '../WarningText/WarningText';

type RelevosStepsDefaultProps = {
  firstStep: string;
  firstStepDescription: string;
  secondStep: string | JSX.Element;
  secondStepDescription?: string;
  isUpgrade: boolean;
  mode: UserSelectMode;
};

const RelevosUserSteps = ({ ...props }: RelevosStepsDefaultProps): JSX.Element | null => {
  const {
    applicationUser,
    quickContact,
    setApplicationUser,
    setQuickContact,
    restaurantId,
    setRestaurantId,
    resetRestaurantId,
    scannedRelevos,
    setScannedRelevos,
    isDeliveryNoteEnabled,
    handleDeliveryNoteEnabled,
    setSelectItemMode,
    selectItemMode,
    mode,
  } = useScannedRelevosContext();
  const { restaurantIds, restaurants, isQuickContactsEnabled } = useRestaurantsData();
  const translations = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef(null);
  const scanUserIdDisclosure = useDisclosure();

  const isClientSelected = useMemo(() => applicationUser || quickContact, [applicationUser, quickContact]);

  const handleClose = () => setIsOpen(false);
  const handleConfirm = () => {
    setApplicationUser(null);
    setQuickContact(null);
    if (scannedRelevos.length) {
      setScannedRelevos([]);
    }
    setIsOpen(false);
  };

  const handleClearRestaurant = () => {
    resetRestaurantId();
  };

  const getIconPath = (iconName: string) => require(`../../../../../assets/icons/${iconName}`);

  const showUserDebtWarning =
    applicationUser && applicationUser.debts.length && applicationUser.debts.some((debt) => debt.amount.isPositive());

  const showUserPaymentMethodInvalidWarning = applicationUser && !applicationUser.paymentInformationValid;

  const handleQuickContactChange = (option: SingleValue<QuickContactResponse>) => {
    if (option) {
      if (option.type === QuickContactType.RESTAURANT) {
        setSelectItemMode(ItemAssignmentSelectMode.CAMERA_SCAN);
      }
      setQuickContact(option);
    }
  };

  return (
    <HStack align="stretch" w={'100%'}>
      <Stepper activeIndex={(!applicationUser && !quickContact) || !restaurantId ? 0 : 1} width={'100%'}>
        <Step index={0}>
          <VStack align="start" spacing="0px" w={'100%'}>
            <HStack align="start" spacing="0px">
              <VStack align="start" textAlign="left" spacing="0px" ml={2}>
                <Text color={!applicationUser && !quickContact ? colors.grey[900] : colors.grey[100]} fontWeight="bold">
                  {props.firstStep}
                </Text>
                <Text color={!applicationUser && !quickContact ? colors.grey[900] : colors.grey[100]}>
                  {props.firstStepDescription}
                </Text>
              </VStack>
            </HStack>

            {!restaurantId ? (
              <Box w={['100%', '100%', '50%', '50%']} px={2} py={[1, 1, 2, 2]}>
                <RestaurantSelect
                  userRestaurants={restaurants}
                  restaurantId={restaurantId}
                  setRestaurantId={setRestaurantId}
                  resetRestaurantId={resetRestaurantId}
                  styles={getAssignSelectStyle<SelectOption>()}
                />
              </Box>
            ) : null}

            <HStack style={!restaurantId ? { display: 'none' } : {}}>
              <Image src={getIconPath('done.svg')} alt={'Done icon'} mr={2} />
              <Text color={restaurantId ? 'grey.900' : 'beige.50'} textAlign="left" fontSize="18px">
                {translations('restaurant')} <b>{restaurants.find((r) => r.id === restaurantId)?.restaurantName}</b>
              </Text>
              {restaurantIds.length > 1 ? (
                <Button variant={'transparent'} color={'orange.500'} onClick={handleClearRestaurant}>
                  {translations('change')}
                </Button>
              ) : null}
            </HStack>

            <Flex w={'100%'} wrap={'wrap'}>
              {isQuickContactsEnabled && !quickContact && !applicationUser && mode === ScannerMode.ASSIGN ? (
                <Box w={['100%', '100%', '50%', '50%']} px={2} py={[1, 1, 2, 2]}>
                  <QuickContactSelect
                    quickContact={quickContact}
                    restaurantId={restaurantId || undefined}
                    type={
                      mode === ScannerMode.ASSIGN && selectItemMode === ItemAssignmentSelectMode.SELECT_CATEGORY
                        ? QuickContactType.USER
                        : undefined
                    }
                    onQuickContactChange={handleQuickContactChange}
                  />
                </Box>
              ) : null}
              {!applicationUser && !quickContact ? (
                <>
                  <Box w={['100%', '100%', '50%', '50%']} px={2} py={[1, 1, 2, 2]}>
                    <Autocomplete placeholder={translations('client_id_input_label')} mode={props.mode} />
                  </Box>
                  {selectItemMode === ItemAssignmentSelectMode.SELECT_CATEGORY && mode === ScannerMode.ASSIGN ? (
                    <Box mb={3} mr={2} w={'100%'} justifyContent={'flex-end'} display={'flex'}>
                      <Button
                        variant="orangeSolid"
                        onClick={scanUserIdDisclosure.onOpen}
                        leftIcon={<Image src={getIconPath('qr_icon.svg')} />}
                      >
                        {translations('scan_user_id_button_label')}
                      </Button>
                    </Box>
                  ) : null}
                </>
              ) : null}
            </Flex>

            <ScanUserIdModal isOpen={scanUserIdDisclosure.isOpen} onClose={scanUserIdDisclosure.onClose} />

            <HStack style={!isClientSelected ? { display: 'none' } : {}}>
              <Image src={getIconPath('done.svg')} alt={'Done icon'} mr={2} />
              <Text fontSize="18px" textAlign="left" color={isClientSelected ? colors.grey[900] : colors.beige[50]}>
                {translations(applicationUser ? 'user' : 'customer')}{' '}
                <b>{applicationUser?.userId || quickContact?.quickContactName}</b>
              </Text>
              <Button variant={'transparent'} color={colors.orange[500]} onClick={() => setIsOpen(true)}>
                {translations('change')}
              </Button>
            </HStack>
            {showUserPaymentMethodInvalidWarning ? (
              <WarningText warningText={translations('no_valid_payment_information')} />
            ) : null}
            {showUserDebtWarning ? <WarningText warningText={translations('user_debt_warning')} /> : null}

            <Dialog
              isOpen={isOpen}
              leastDestructiveRef={cancelRef}
              onClose={handleClose}
              dialogHeader={<Text>{translations('scan_clear_client_id_confirm')}</Text>}
              onConfirm={handleConfirm}
              cancelText={translations('scan_remove_cancel')}
              confirmText={translations('scan_remove_confirmed')}
            >
              <Text>{translations('scan_clear_client_id_confirm_description')}</Text>
            </Dialog>

            {quickContact ? (
              <>
                <Checkbox colorScheme="orange" isChecked={isDeliveryNoteEnabled} onChange={handleDeliveryNoteEnabled}>
                  {translations('delivery_note_checkbox')}
                </Checkbox>
                <Text fontSize={12} color={'grey.200'}>
                  {translations('delivery_note_checkbox_info')}
                </Text>
              </>
            ) : null}
          </VStack>
        </Step>

        <Step index={1}>
          <VStack
            alignItems="start"
            textAlign="left"
            display={'flex'}
            spacing="0px"
            my={'auto'}
            ml={2}
            mb={props.secondStepDescription && isClientSelected ? -6 : 0}
          >
            <Text color={isClientSelected ? colors.grey[900] : colors.grey[100]} fontWeight="bold">
              {props.secondStep}
            </Text>
            {props.secondStepDescription && isClientSelected ? (
              <Box color={isClientSelected ? colors.grey[900] : colors.grey[100]}>{props.secondStepDescription}</Box>
            ) : null}
          </VStack>
        </Step>
      </Stepper>
    </HStack>
  );
};
export default RelevosUserSteps;
